(ns hai.ui.components.datetime
  "Framework-agnostic helpers for the date-picker's value: converting between a
   picker selection `{:date {:year :month :day} :time <minutes|nil>}` and an
   absolute moment `{:at <js/Date> :all-day? <bool> :timezone <iana-string>}`,
   plus display formatting. No re-frame, reusable anywhere.")

(defn browser-timezone []
  (.. (js/Intl.DateTimeFormat.) (resolvedOptions) -timeZone))

(defn value->moment
  "Picker value → absolute moment, or nil when no date is picked. All-day when
   `:time` is absent."
  [{:keys [date time]}]
  (when date
    (let [{:keys [year month day]} date
          datetime? (some? time)]
      {:at (js/Date. year (dec month) day
                     (if datetime? (quot time 60) 0)
                     (if datetime? (mod time 60) 0)
                     0)
       :all-day? (not datetime?)
       :timezone (browser-timezone)})))

(defn- instant->parts [millis tz]
  ;; Intl throws a RangeError on a null timeZone, and an all-day moment can
  ;; reach us without one (`:task/deadline-timezone` is optional on create), so
  ;; fall back to the viewer's zone rather than blow up every caller.
  (let [fmt (js/Intl.DateTimeFormat.
             "en-US" #js {:timeZone (or tz (browser-timezone))
                          :year "numeric" :month "numeric"
                          :day "numeric" :hour "numeric" :minute "numeric"
                          :hourCycle "h23"})
        parts (->> (.formatToParts fmt (js/Date. millis))
                   (reduce (fn [acc part] (assoc acc (.-type part) (.-value part))) {}))]
    (into {} (map (fn [k] [k (js/parseInt (get parts (name k)) 10)]))
          [:year :month :day :hour :minute])))

(defn moment->value
  "Inverse of `value->moment`, for pre-filling the picker. All-day moments read
   in their stored zone, timed ones in the viewer's zone."
  [{:keys [at all-day? timezone]}]
  (when at
    (let [tz (if all-day? timezone (browser-timezone))
          {:keys [year month day hour minute]} (instant->parts (.getTime at) tz)]
      (cond-> {:date {:year year :month month :day day}}
        (not all-day?) (assoc :time (+ (* hour 60) minute))))))

(defn- today-parts []
  (let [now (js/Date.)]
    {:year (.getFullYear now) :month (inc (.getMonth now)) :day (.getDate now)}))

(defn- parts->utc [{:keys [year month day]}]
  (js/Date.UTC year (dec month) day))

(defn- absolute-day-label [{:keys [year] :as parts} today]
  (.toLocaleDateString
   (js/Date. (parts->utc parts)) "en-US"
   (clj->js (cond-> {:month "short" :day "numeric" :timeZone "UTC"}
              (not= year (:year today)) (assoc :year "numeric")))))

(defn- day-diff [parts today]
  (js/Math.round (/ (- (parts->utc parts) (parts->utc today)) 86400000)))

(defn- relative-day-label [parts today]
  (let [diff (day-diff parts today)]
    (cond
      (== diff 0) "today"
      (== diff 1) "tomorrow"
      (== diff -1) "yesterday"
      :else (absolute-day-label parts today))))

(defn relative-parts
  "The pieces of [[relative-label]]: `:day` — 'today', 'tomorrow',
   'yesterday' or 'Jun 8' (with year when it differs) — and, for a timed
   moment, `:time` — '4:00 PM'. nil when `:at` is absent."
  [{:keys [at all-day? timezone]}]
  (when at
    (let [millis (.getTime at)
          tz (if all-day? timezone (browser-timezone))]
      (cond-> {:day (relative-day-label (instant->parts millis tz) (today-parts))}
        (not all-day?)
        (assoc :time (.toLocaleTimeString (js/Date. millis) "en-US"
                                          #js {:hour "numeric" :minute "2-digit"}))))))

(defn relative-label
  "Friendly label: 'today'/'tomorrow'/'yesterday' or 'Jun 8' (with year when it
   differs), plus ' · 4:00 PM' for timed moments. nil when `:at` is absent."
  [moment]
  (when-let [{:keys [day time]} (relative-parts moment)]
    (cond-> day
      time (str " · " time))))

(defn today?
  "True when the moment falls on the current local day. All-day moments are read
   in the zone they were authored in — that's the day they were meant for — and
   timed ones in the viewer's zone, the same split `relative-label` makes."
  [{:keys [at all-day? timezone]}]
  (boolean
   (when at
     (let [tz (if all-day? timezone (browser-timezone))]
       (= (select-keys (instant->parts (.getTime at) tz) [:year :month :day])
          (today-parts))))))

(defn days-from-today
  "Whole days from today to the moment's day: 0 today, 1 tomorrow, -1
   yesterday, read in the same zones `relative-label` reads it in. nil when
   `:at` is absent."
  [{:keys [at all-day? timezone]}]
  (when at
    (let [tz (if all-day? timezone (browser-timezone))]
      (day-diff (instant->parts (.getTime at) tz) (today-parts)))))

(defn date-label
  "Absolute date like 'Jun 8' (with the year when it differs from the current
   one) — never 'today'/'tomorrow'. Ignores any time-of-day. nil when `:at` is
   absent."
  [{:keys [at all-day? timezone]}]
  (when at
    (let [tz (if all-day? timezone (browser-timezone))]
      (absolute-day-label (instant->parts (.getTime at) tz) (today-parts)))))

(def ^:private month-abbrevs
  ["Jan" "Feb" "Mar" "Apr" "May" "Jun" "Jul" "Aug" "Sep" "Oct" "Nov" "Dec"])

(defn- pad [n]
  (.padStart (str n) 2 "0"))

(defn tooltip
  "Raw timestamp like '08-Jun-2026 16:00', or just the date for all-day moments.
   nil when `:at` is absent."
  [{:keys [at all-day? timezone]}]
  (when at
    (let [tz (if all-day? timezone (browser-timezone))
          {:keys [year month day hour minute]} (instant->parts (.getTime at) tz)
          date (str (pad day) "-" (nth month-abbrevs (dec month)) "-" year)]
      (if all-day?
        date
        (str date " " (pad hour) ":" (pad minute))))))

(defn day-time-label
  "When something happened, like '22 Sep, 16:04', in the viewer's zone and
   without the year. nil for nil. Read through a native Date: the web-app's
   transit hands instants over as `goog.date.UtcDateTime`, whose getters
   answer in UTC."
  [instant]
  (when instant
    (let [local (js/Date. (.getTime instant))]
      (str (.getDate local) " " (nth month-abbrevs (.getMonth local)) ", "
           (pad (.getHours local)) ":" (pad (.getMinutes local))))))

(defn- counted
  [n unit]
  (str n " " unit (when-not (= 1 n) "s")))

(defn duration-label
  "How long since `instant`, like '12 minutes' or '1 hour 5 minutes'. nil for
   nil."
  [instant]
  (when instant
    (let [seconds (max 0 (js/Math.round (/ (- (js/Date.now) (.getTime instant)) 1000)))
          minutes (js/Math.round (/ seconds 60))
          hours (js/Math.floor (/ minutes 60))
          rest-minutes (mod minutes 60)]
      (cond
        (< seconds 60) (counted seconds "second")
        (< minutes 60) (counted minutes "minute")
        (zero? rest-minutes) (counted hours "hour")
        :else (str (counted hours "hour") " " (counted rest-minutes "minute"))))))

(defn clock-icon []
  [:svg {:width "13" :height "13" :viewBox "0 0 24 24"
         :stroke-linecap "round" :stroke-linejoin "round"
         :style {:fill "none" :stroke "currentColor" :stroke-width "2"}}
   [:circle {:cx "12" :cy "12" :r "9" :style {:fill "none"}}]
   [:polyline {:points "12 7 12 12 15 14"}]])
